import {AlarmType, SubscriptionAlertType} from "@/models/Enums";
import BaseAlarmModel from "@/models/BaseAlarmModel";
import BaseAlarmDeviceModel from "@/models/BaseAlarmDeviceModel";
import UnleashHelper from "@/helpers/UnleashHelper";
import RentaEasyConstants from "@/helpers/RentaEasyConstants";

type SelectedDevicesConfig = {
    type: SubscriptionAlertType;
    getConfiguredAlert: (device: BaseAlarmDeviceModel<BaseAlarmModel>) => BaseAlarmModel | undefined;
    supported: (device: BaseAlarmDeviceModel<BaseAlarmModel>) => boolean;
    supportsUpdate: boolean;
}

export class AlarmTypeHelper {
    private static readonly fuelAlarmTypes = [AlarmType.TrackUnitFuelLevelGreaterThan, AlarmType.TrackUnitFuelLevelLessThan, AlarmType.PindoraFluidLevelLessThan];
    private static readonly batteryAlarmTypes = [AlarmType.TrackUnitBatteryVoltageGreaterThan, AlarmType.TrackUnitBatteryVoltageLessThan];
    private static readonly theftAlarmTypes = [AlarmType.TrackUnitMovementBasedTheft];
    private static readonly idleAlarmTypes = [AlarmType.DeviceUnusedFor3Days];
    private static readonly environmentControlStructureTemperatureAlarmTypes = [AlarmType.EnvironmentControlExternalStructureTemperature];
    private static readonly environmentControlAirTemperatureAlarmTypes = [AlarmType.EnvironmentControlInternalStructureTemperature, AlarmType.EnvironmentControlAirTemperature1];
    private static readonly environmentControlStructureHumidityAlarmTypes = [AlarmType.EnvironmentControlExternalStructureHumidity];
    private static readonly environmentControlAirHumidityAlarmTypes = [AlarmType.EnvironmentControlInternalStructureHumidity, AlarmType.EnvironmentControlAirHumidity];

    private static _selectedDevicesConfig: SelectedDevicesConfig[] = [];

    private static readonly subscriptionAlertTypeToAlarmTypeMap = {
        [SubscriptionAlertType.Fuel]: AlarmTypeHelper.fuelAlarmTypes,
        [SubscriptionAlertType.Battery]: AlarmTypeHelper.batteryAlarmTypes,
        [SubscriptionAlertType.Theft]: AlarmTypeHelper.theftAlarmTypes,
        [SubscriptionAlertType.Idle]: AlarmTypeHelper.idleAlarmTypes,
        [SubscriptionAlertType.EnvironmentControlStructureTemperature]: AlarmTypeHelper.environmentControlStructureTemperatureAlarmTypes,
        [SubscriptionAlertType.EnvironmentControlAirTemperature]: AlarmTypeHelper.environmentControlAirTemperatureAlarmTypes,
        [SubscriptionAlertType.EnvironmentControlStructureHumidity]: AlarmTypeHelper.environmentControlStructureHumidityAlarmTypes,
        [SubscriptionAlertType.EnvironmentControlAirHumidity]: AlarmTypeHelper.environmentControlAirHumidityAlarmTypes,
    };

    public static containsTheftAlarmTypes = (alarmTypes: AlarmType[]): boolean => {
        return AlarmTypeHelper.containsAlarmTypes(alarmTypes, AlarmTypeHelper.theftAlarmTypes);
    };

    public static containsIdleAlarmTypes = (alarmTypes: AlarmType[]): boolean => {
        return AlarmTypeHelper.containsAlarmTypes(alarmTypes, AlarmTypeHelper.idleAlarmTypes);
    };

    public static containsFuelAlarmTypes = (alarmTypes: AlarmType[]): boolean => {
        return AlarmTypeHelper.containsAlarmTypes(alarmTypes, AlarmTypeHelper.fuelAlarmTypes);
    };

    public static containsEnvironmentControlStructureTemperatureAlarmTypes = (alarmTypes: AlarmType[]): boolean => {
        return AlarmTypeHelper.containsAlarmTypes(alarmTypes, AlarmTypeHelper.environmentControlStructureTemperatureAlarmTypes);
    };

    public static containsEnvironmentControlAirTemperatureAlarmTypes = (alarmTypes: AlarmType[]): boolean => {
        return AlarmTypeHelper.containsAlarmTypes(alarmTypes, AlarmTypeHelper.environmentControlAirTemperatureAlarmTypes);
    };

    public static containsEnvironmentControlStructureHumidityAlarmTypes = (alarmTypes: AlarmType[]): boolean => {
        return AlarmTypeHelper.containsAlarmTypes(alarmTypes, AlarmTypeHelper.environmentControlStructureHumidityAlarmTypes);
    };

    public static containsEnvironmentControlAirHumidityAlarmTypes = (alarmTypes: AlarmType[]): boolean => {
        return AlarmTypeHelper.containsAlarmTypes(alarmTypes, AlarmTypeHelper.environmentControlAirHumidityAlarmTypes);
    };

    public static containsBatteryAlarmTypes = (alarmTypes: AlarmType[]): boolean => {
        return AlarmTypeHelper.containsAlarmTypes(alarmTypes, AlarmTypeHelper.batteryAlarmTypes);
    };

    public static getConfiguredAlertBySubscriptionType = (configuredAlerts: BaseAlarmModel[], type: SubscriptionAlertType): BaseAlarmModel | undefined => {
        return AlarmTypeHelper.getConfiguredAlertByType(configuredAlerts, AlarmTypeHelper.subscriptionAlertTypeToAlarmTypeMap[type]);
    };

    public static getAlarmTypeBySubscriptionType = (supportedAlarmTypes: AlarmType[], type: SubscriptionAlertType): AlarmType => {
        return supportedAlarmTypes.find(supportedAlarmType => AlarmTypeHelper.subscriptionAlertTypeToAlarmTypeMap[type].includes(supportedAlarmType))!;
    }

    public static get selectedDevicesConfig(): SelectedDevicesConfig[] {
        return AlarmTypeHelper._selectedDevicesConfig.length > 0 ? AlarmTypeHelper._selectedDevicesConfig : (AlarmTypeHelper._selectedDevicesConfig = [
            {
                type: SubscriptionAlertType.Fuel,
                getConfiguredAlert: (device: BaseAlarmDeviceModel<BaseAlarmModel>) => AlarmTypeHelper.getConfiguredAlertBySubscriptionType(device.configuredAlerts, SubscriptionAlertType.Fuel),
                supported: (device: BaseAlarmDeviceModel<BaseAlarmModel>) => AlarmTypeHelper.containsFuelAlarmTypes(device.supportedAlarmTypes),
                supportsUpdate: false,
            },
            {
                type: SubscriptionAlertType.Battery,
                getConfiguredAlert: (device: BaseAlarmDeviceModel<BaseAlarmModel>) => AlarmTypeHelper.getConfiguredAlertBySubscriptionType(device.configuredAlerts, SubscriptionAlertType.Battery),
                supported: (device: BaseAlarmDeviceModel<BaseAlarmModel>) => AlarmTypeHelper.containsBatteryAlarmTypes(device.supportedAlarmTypes),
                supportsUpdate: false,
            },
            {
                type: SubscriptionAlertType.Theft,
                getConfiguredAlert: (device: BaseAlarmDeviceModel<BaseAlarmModel>) => AlarmTypeHelper.getConfiguredAlertBySubscriptionType(device.configuredAlerts, SubscriptionAlertType.Theft),
                supported: (device: BaseAlarmDeviceModel<BaseAlarmModel>) => AlarmTypeHelper.containsTheftAlarmTypes(device.supportedAlarmTypes),
                supportsUpdate: false,
            },
            {
                type: SubscriptionAlertType.Idle,
                getConfiguredAlert: (device: BaseAlarmDeviceModel<BaseAlarmModel>) => AlarmTypeHelper.getConfiguredAlertBySubscriptionType(device.configuredAlerts, SubscriptionAlertType.Idle),
                supported: (device: BaseAlarmDeviceModel<BaseAlarmModel>) => AlarmTypeHelper.containsIdleAlarmTypes(device.supportedAlarmTypes),
                supportsUpdate: false,
            },
            ...(UnleashHelper.isEnabled(RentaEasyConstants.featureFlagEnvironmentControlAlerts) ? [
                {
                    type: SubscriptionAlertType.EnvironmentControlStructureTemperature,
                    getConfiguredAlert: (device: BaseAlarmDeviceModel<BaseAlarmModel>) => AlarmTypeHelper.getConfiguredAlertBySubscriptionType(device.configuredAlerts, SubscriptionAlertType.EnvironmentControlStructureTemperature),
                    supported: (device: BaseAlarmDeviceModel<BaseAlarmModel>) => AlarmTypeHelper.containsEnvironmentControlStructureTemperatureAlarmTypes(device.supportedAlarmTypes),
                    supportsUpdate:  true,
                },
                {
                    type: SubscriptionAlertType.EnvironmentControlAirTemperature,
                    getConfiguredAlert: (device: BaseAlarmDeviceModel<BaseAlarmModel>) => AlarmTypeHelper.getConfiguredAlertBySubscriptionType(device.configuredAlerts, SubscriptionAlertType.EnvironmentControlAirTemperature),
                    supported: (device: BaseAlarmDeviceModel<BaseAlarmModel>) => AlarmTypeHelper.containsEnvironmentControlAirTemperatureAlarmTypes(device.supportedAlarmTypes),
                    supportsUpdate:  true,
                },
                {
                    type: SubscriptionAlertType.EnvironmentControlStructureHumidity,
                    getConfiguredAlert: (device: BaseAlarmDeviceModel<BaseAlarmModel>) => AlarmTypeHelper.getConfiguredAlertBySubscriptionType(device.configuredAlerts, SubscriptionAlertType.EnvironmentControlStructureHumidity),
                    supported: (device: BaseAlarmDeviceModel<BaseAlarmModel>) => AlarmTypeHelper.containsEnvironmentControlStructureHumidityAlarmTypes(device.supportedAlarmTypes),
                    supportsUpdate:  true,
                },
                {
                    type: SubscriptionAlertType.EnvironmentControlAirHumidity,
                    getConfiguredAlert: (device: BaseAlarmDeviceModel<BaseAlarmModel>) => AlarmTypeHelper.getConfiguredAlertBySubscriptionType(device.configuredAlerts, SubscriptionAlertType.EnvironmentControlAirHumidity),
                    supported: (device: BaseAlarmDeviceModel<BaseAlarmModel>) => AlarmTypeHelper.containsEnvironmentControlAirHumidityAlarmTypes(device.supportedAlarmTypes),
                    supportsUpdate:  true,
                },
            ] : []),
        ]);
    };

    private static getConfiguredAlertByType = (configuredAlerts: BaseAlarmModel[], types: AlarmType[]): BaseAlarmModel | undefined => {
        if (!configuredAlerts) {
            return undefined;
        }
        return configuredAlerts.find(alert => types.some(type => type === alert.alarmType));
    };


    private static containsAlarmTypes = (alarmTypes: AlarmType[], expectedAlarmTypes: AlarmType[]): boolean => {
        if (!alarmTypes) {
            return false;
        }
        return expectedAlarmTypes.some(expectedAlarmType => alarmTypes.includes(expectedAlarmType));
    };
}